/**
 * Timeline, note, task and assignment contracts. Destination: `@renote/contracts`.
 *
 * Every one of these hangs off exactly one subject. `subjectRef` is the shared
 * shape; the server maps it onto the corresponding nullable FK column, which is
 * what keeps the single-subject check constraint satisfiable and gives
 * UnitOfWork the single entity id it requires.
 */

import { z } from 'zod'
import { arrayParam, boolParam, instant, pageOf, pageQuery, uuid } from './common'

export const subjectType = z.enum(['lead', 'deal', 'organization', 'contact'])
export type SubjectType = z.infer<typeof subjectType>

export const subjectRef = z.object({
  type: subjectType,
  id: uuid,
})
export type SubjectRef = z.infer<typeof subjectRef>

export const activityKind = z.enum([
  'note',
  'note_deleted',
  'note_restored',
  'email',
  'call',
  'meeting',
  'task',
  'task_deleted',
  'task_restored',
  'status_change',
  'assignment',
  'field_change',
])
export type ActivityKind = z.infer<typeof activityKind>

/**
 * Kind-specific payloads. Discriminated so the timeline renderer gets a real
 * narrowed type per row instead of `Record<string, unknown>`.
 */
export const activityPayload = z.discriminatedUnion('kind', [
  /**
   * `title` is a snapshot, not a join. A timeline entry that only holds an id
   * becomes unreadable the moment the note is deleted — and an audit trail whose
   * older entries decay is not an audit trail. Every payload below is
   * self-describing for the same reason.
   */
  z.object({ kind: z.literal('note'), noteId: uuid, title: z.string().nullable() }),
  z.object({ kind: z.literal('note_deleted'), noteId: uuid, title: z.string().nullable() }),
  z.object({ kind: z.literal('note_restored'), noteId: uuid, title: z.string().nullable() }),
  z.object({
    kind: z.literal('email'),
    subject: z.string(),
    snippet: z.string(),
    direction: z.enum(['inbound', 'outbound']),
  }),
  z.object({
    kind: z.literal('call'),
    direction: z.enum(['inbound', 'outbound']),
    durationSeconds: z.number().int().nonnegative().nullable(),
    outcome: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('meeting'),
    title: z.string(),
    startsAt: instant,
    endsAt: instant.nullable(),
  }),
  z.object({ kind: z.literal('task'), taskId: uuid, title: z.string() }),
  z.object({ kind: z.literal('task_deleted'), taskId: uuid, title: z.string() }),
  z.object({ kind: z.literal('task_restored'), taskId: uuid, title: z.string() }),
  z.object({
    kind: z.literal('status_change'),
    fromStatusId: uuid.nullable(),
    toStatusId: uuid,
  }),
  z.object({
    kind: z.literal('assignment'),
    userId: uuid,
    action: z.enum(['assigned', 'unassigned']),
  }),
  z.object({
    kind: z.literal('field_change'),
    field: z.string(),
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
])
export type ActivityPayload = z.infer<typeof activityPayload>

export const activity = z.object({
  id: uuid,
  kind: activityKind,
  payload: activityPayload,
  occurredAt: instant,
  actorId: uuid.nullable(),
  subject: subjectRef,
  createdAt: instant,
})
export type Activity = z.infer<typeof activity>

export const timelineQuery = pageQuery
  .extend({
    subject: subjectRef,
    kind: arrayParam(activityKind).optional(),
    before: instant.optional(),
  })
  .strict()
export type TimelineQuery = z.infer<typeof timelineQuery>

export const timelineResult = pageOf(activity)
export type TimelineResult = z.infer<typeof timelineResult>


export const note = z.object({
  id: uuid,
  title: z.string().nullable(),
  body: z.string(),
  subject: subjectRef,
  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
  /** Set together, or not at all — see crm_note_deletion_is_attributed. */
  deletedAt: instant.nullable(),
  deletedBy: uuid.nullable(),
})
export type Note = z.infer<typeof note>

export const createNoteInput = z
  .object({
    subject: subjectRef,
    title: z.string().trim().max(200).nullable(),
    body: z.string().trim().min(1),
  })
  .strict()
export type CreateNoteInput = z.infer<typeof createNoteInput>

export const updateNoteInput = z
  .object({
    title: z.string().trim().max(200).nullable().optional(),
    body: z.string().trim().min(1).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
export type UpdateNoteInput = z.infer<typeof updateNoteInput>

export const noteSortField = z.enum(['createdAt', 'updatedAt'])

/**
 * Subject arrives flat (`?subjectType=lead&subjectId=…`) rather than as a nested
 * object, because a query string cannot carry one. Timeline endpoints don't need
 * this — they take the subject from the path.
 */
export const noteListQuery = pageQuery
  .extend({
    subjectType,
    subjectId: uuid,
    search: z.string().trim().max(200).optional(),
    /** The restore view opts in; every other caller sees only live notes. */
    includeDeleted: boolParam.default(false),
    sort: noteSortField.default('createdAt'),
    direction: z.enum(['asc', 'desc']).default('desc'),
  })
  .strict()
export type NoteListQuery = z.infer<typeof noteListQuery>

export const noteListResult = pageOf(note)
export type NoteListResult = z.infer<typeof noteListResult>


export const taskStatus = z.enum(['backlog', 'todo', 'in_progress', 'done', 'cancelled'])
export const taskPriority = z.enum(['low', 'medium', 'high'])
export type TaskStatus = z.infer<typeof taskStatus>
export type TaskPriority = z.infer<typeof taskPriority>

export const task = z.object({
  id: uuid,
  title: z.string(),
  description: z.string().nullable(),
  status: taskStatus,
  priority: taskPriority,
  dueDate: instant.nullable(),
  assignedTo: uuid.nullable(),
  completedAt: instant.nullable(),
  subject: subjectRef,
  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
  deletedAt: instant.nullable(),
  deletedBy: uuid.nullable(),
})
export type Task = z.infer<typeof task>

export const createTaskInput = z
  .object({
    subject: subjectRef,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().nullable(),
    status: taskStatus.default('todo'),
    priority: taskPriority.default('medium'),
    dueDate: instant.nullable(),
    assignedTo: uuid.nullable(),
  })
  .strict()
export type CreateTaskInput = z.infer<typeof createTaskInput>

/** `completedAt` is absent by design — the database derives it from status. */
export const updateTaskInput = createTaskInput
  .omit({ subject: true })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
export type UpdateTaskInput = z.infer<typeof updateTaskInput>

export const taskSortField = z.enum(['dueDate', 'createdAt', 'updatedAt', 'priority'])

export const taskListQuery = pageQuery
  .extend({
    /** Omit both for the cross-entity "my tasks" view. */
    subjectType: subjectType.optional(),
    subjectId: uuid.optional(),
    assignedTo: arrayParam(uuid).optional(),
    status: arrayParam(taskStatus).optional(),
    priority: arrayParam(taskPriority).optional(),
    dueBefore: instant.optional(),
    openOnly: boolParam.default(true),
    includeDeleted: boolParam.default(false),
    sort: taskSortField.default('dueDate'),
    direction: z.enum(['asc', 'desc']).default('asc'),
  })
  .strict()
  .refine((v) => (v.subjectType === undefined) === (v.subjectId === undefined), {
    message: 'Provide both subjectType and subjectId, or neither',
    path: ['subjectId'],
  })
export type TaskListQuery = z.infer<typeof taskListQuery>

export const taskListResult = pageOf(task)
export type TaskListResult = z.infer<typeof taskListResult>


/**
 * Assignment is distinct from ownership: `ownerId` is single and commercial,
 * assignment is many and operational.
 */
export const assignment = z.object({
  id: uuid,
  userId: uuid,
  subject: subjectRef,
  assignedBy: uuid,
  createdAt: instant,
})
export type Assignment = z.infer<typeof assignment>

export const assignmentList = z.array(assignment)
export type AssignmentList = z.infer<typeof assignmentList>

/**
 * Bulk-safe by shape, per-entity by execution: UnitOfWork rejects writes it
 * cannot attach to one entity id, so the handler loops inside a single
 * transaction rather than issuing one multi-row statement.
 */
export const setAssigneesInput = z
  .object({
    subject: subjectRef,
    userIds: z.array(uuid).max(50),
  })
  .strict()
export type SetAssigneesInput = z.infer<typeof setAssigneesInput>
